import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import PropTypes from 'prop-types';
import { Field, Form, Formik } from 'formik';
import { createStructuredSelector } from 'reselect';
import { connect, useDispatch } from 'react-redux';
import { object, string } from 'yup';
import get from 'lodash/get';
import FormikTextField from '../Fields/TextField';
import { ARTIST_TYPE_ID, PROFESSION, PROFESSIONS_AVAILABILITY, ROLE, TP, VISIBILITY } from '../../constants/consts';
import { useTranslation } from '../../src/i18n';
import SubmitModalLayout from '../SubmitModalLayout';
import EditModeCard, { EditModeCardSection } from '../EditModeCard';
import ArtistProfessionFields from '../EditComponents/ArtistProfessionFields';
import {
  selectArtistProfessionsOptions,
  selectBufferValueData,
  selectVoiceTypesOptions,
} from '../../containers/App/selectors';
import {
  createEntity,
  getArtistProfessionsAction,
  getArtistVoiceTypesAction,
  getList,
  useBufferValueAction,
} from '../../containers/App/actions';
import { PROFESSION_QUALIFICATIONS_ENDPOINT, PROFILES_ENDPOINT } from '../../constants/endpointConstants';
import { getArtistsSuggestionsAction } from '../../containers/ProductionUpdateSteps/actions';
import SuggestionArtistsModal from '../../containers/Managers/editComponents/AgencyArtistAddForm/SuggestionArtistsModal';
import { selectArtistsSuggestions } from '../../containers/ProductionUpdateSteps/selectors';
import { checkDuplicateName } from '../../utils/editCommon';

const useStyles = makeStyles(theme => ({
  root: {
    margin: theme.spacing(3),
    padding: `0 ${theme.spacing()}px ${theme.spacing()}px`,
  },
  bottomFields: {
    marginBottom: '200px',
  },
  dropdownClass: {
    '&>div': {
      maxHeight: '200px',
    },
  },
  errorMessage: {
    bottom: '-20px',
    position: 'absolute',
  },
  professionBlock: {
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'row',
    },
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
}));

const { firstName, lastName, professionQualification, profession } = {
  firstName: {
    name: 'firstName',
    label: `${TP}.FN_FIRST_MIDDLE_NAME`,
  },
  lastName: {
    name: 'lastName',
    label: `${TP}.m_REGISTRATION_LASTNAME`,
  },
  professionQualification: {
    name: 'professionQualification',
    label: `${TP}.m_VOICE`,
  },
  profession: {
    name: 'profession',
    label: `${TP}.m_JOBTYPE`,
  },
};

const AddNewArtistModal = ({
  cast,
  onClose,
  voiceTypes,
  professions,
  submitFunc,
  artistsSuggestions,
  artistCreation,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formRef = React.createRef(null);
  const [artistsSuggestionsModalValue, setArtistsSuggestionsModalValue] = React.useState(null);

  const initFormValues = React.useCallback(
    () => ({
      firstName: '',
      lastName: '',
      professionQualification:
        cast?.value?.type === 'role' && voiceTypes.length
          ? voiceTypes.find(el => el.value === cast?.value?.professionQualification?.id)
          : '',
      profession:
        cast?.value?.type === 'profession' && professions.length
          ? professions.find(el => el.value === cast?.value?.id)
          : '',
    }),
    [cast, voiceTypes, professions],
  );

  useEffect(() => {
    if (!voiceTypes.length) {
      dispatch(
        getList({
          endpoint: PROFESSION_QUALIFICATIONS_ENDPOINT,
          sagaRoutine: getArtistVoiceTypesAction,
          params: { queryParams: {}, pagination: { limit: 100 } },
        }),
      );
    }
    if (!professions.length) {
      dispatch(getArtistProfessionsAction({ query: { availability: PROFESSIONS_AVAILABILITY.UPDATE_PROFILE } }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatArtistData = values => {
    const { firstName: name, lastName: surname, profession: prof, professionQualification: voice } = values;
    return {
      ...{
        firstName: name,
        lastName: surname,
        name: `${name} ${surname}`,
        isHidden: false,
        profileType: { id: ARTIST_TYPE_ID },
        user: null,
      },
      ...(prof ? { profession: { id: prof.value } } : {}),
      ...(voice ? { profession: { id: ARTIST_TYPE_ID, qualification: { id: voice.value } } } : {}),
    };
  };

  const saveArtist = formValues => {
    dispatch(
      createEntity({
        endpoint: PROFILES_ENDPOINT,
        sagaRoutine: useBufferValueAction,
        params: formatArtistData(formValues),
        successMessage: t(`${TP}.SUCCESS_CREATING_ARTIST`),
        callback: value => {
          submitFunc({ cast, value });
          onClose();
        },
      }),
    );
  };

  const getArtistsSuggestions = values => {
    const formValues = formRef?.current?.values;
    dispatch(
      getList({
        endpoint: PROFILES_ENDPOINT,
        sagaRoutine: getArtistsSuggestionsAction,
        params: {
          queryParams: {
            profile_type_id: ARTIST_TYPE_ID,
            query: `${values[firstName.name]?.trim()} ${values[lastName.name]?.trim()}`,
            show_invalid: true,
            visibility: VISIBILITY.ALL,
          },
        },
        callback: artists => {
          if (artists?.data?.length > 0) {
            setArtistsSuggestionsModalValue(formValues);
          } else {
            saveArtist(formValues);
          }
        },
      }),
    );
  };

  return (
    <SubmitModalLayout
      isOpened
      onClose={onClose}
      title1={t(`${TP}.FN_ADD_ARTIST`)}
      submitFunc={() => {
        get(formRef?.current, 'submitForm', () => {})();
      }}
      isLoading={artistCreation?.loading}
    >
      <EditModeCard className={classes.root}>
        <EditModeCardSection first>
          <Formik
            initialValues={initFormValues()}
            onSubmit={values => {
              getArtistsSuggestions(values);
            }}
            enableReinitialize
            validationSchema={addNewArtistSchema}
            innerRef={formRef}
          >
            {({ setValues, values }) => (
              <Form>
                <Grid container spacing={3} item lg={8} xs={12}>
                  <Grid item sm={6} xs={12}>
                    <Field
                      name={firstName.name}
                      component={FormikTextField}
                      t={t}
                      label={t(firstName.label)}
                      nativeInputProps={{ maxLength: 255 }}
                      FormHelperTextProps={{ className: classes.errorMessage }}
                    />
                  </Grid>
                  <Grid item sm={6} xs={12}>
                    <Field
                      name={lastName.name}
                      component={FormikTextField}
                      t={t}
                      label={t(lastName.label)}
                      nativeInputProps={{ maxLength: 255 }}
                      FormHelperTextProps={{ className: classes.errorMessage }}
                    />
                  </Grid>
                  <Grid item xs={12} classes={{ root: classes.bottomFields }}>
                    <ArtistProfessionFields
                      setValues={setValues}
                      values={values}
                      voiceTypes={voiceTypes}
                      professions={professions}
                      formikFieldProps={{ dropdownClass: classes.dropdownClass }}
                      voiceField={professionQualification}
                      professionField={profession}
                      className={classes.professionBlock}
                    />
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </EditModeCardSection>
      </EditModeCard>
      {artistsSuggestionsModalValue && (
        <SuggestionArtistsModal
          t={t}
          closeModal={() => setArtistsSuggestionsModalValue(null)}
          isOpened
          addToRoster={() => saveArtist(artistsSuggestionsModalValue)}
          selectArtist={selectedArtist => {
            submitFunc({ cast, value: selectedArtist });
            onClose();
          }}
          agencyData={{}}
          artistSuggestionsList={artistsSuggestions}
          mainText={`${TP}.FN_ARTIST_SUGGESTIONS_MAIN_TEXT`}
          confirmButtonText={`${TP}.CREATE_ADD_TO_CAST`}
          type={
            artistsSuggestions.length === 1 && checkDuplicateName(artistsSuggestions[0], artistsSuggestionsModalValue)
              ? 'duplicate'
              : 'list'
          }
        />
      )}
    </SubmitModalLayout>
  );
};

AddNewArtistModal.propTypes = {
  cast: PropTypes.shape({
    fieldName: PropTypes.string,
    value: PropTypes.shape({
      type: PropTypes.oneOf([PROFESSION, ROLE]),
      id: PropTypes.number,
      professionQualification: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  }),
  onClose: PropTypes.func,
  submitFunc: PropTypes.func,
  artistCreation: PropTypes.object,
};

AddNewArtistModal.defaultProps = {
  onClose: () => {},
  submitFunc: () => {},
  cast: null,
};

const mapStateToProps = createStructuredSelector({
  voiceTypes: selectVoiceTypesOptions(),
  professions: selectArtistProfessionsOptions(),
  artistsSuggestions: selectArtistsSuggestions(),
  artistCreation: selectBufferValueData(),
});

const withConnect = connect(mapStateToProps, null);

export default withConnect(AddNewArtistModal);

const addNewArtistSchema = () =>
  object().shape({
    [firstName.name]: string()
      .nullable()
      .required(`${TP}.FN_REQUIRED_FIELD`),
    [lastName.name]: string()
      .nullable()
      .required(`${TP}.FN_REQUIRED_FIELD`),
  });
